import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Building, CalendarCheck, MessageSquare, DollarSign, Plus, Settings, 
  ChevronRight, Eye, Loader2, CheckCircle, XCircle, Clock, Home,
  Crown, Star, AlertTriangle, CreditCard, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import Layout from '../components/Layout';
import { propertiesApi, bookingsApi, messagesApi, subscriptionsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user) {
      fetchData();
    }
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, conversationsRes] = await Promise.all([
        bookingsApi.getAll(),
        messagesApi.getConversations()
      ]);
      setBookings(bookingsRes.data);
      setConversations(conversationsRes.data);

      if (user?.role === 'owner') {
        const [propsRes, subRes] = await Promise.all([
          propertiesApi.getMine(),
          subscriptionsApi.getMySubscription()
        ]);
        setProperties(propsRes.data);
        setSubscription(subRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? Your listings will become inactive at the end of your billing period.')) {
      return;
    }
    
    setCancelLoading(true);
    try {
      await subscriptionsApi.cancel(false);
      toast.success('Subscription will be canceled at the end of your billing period');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleBookingAction = async (bookingId, status) => {
    try {
      await bookingsApi.updateStatus(bookingId, status);
      toast.success(`Booking ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="h-96 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
        </div>
      </Layout>
    );
  }

  const isOwner = user?.role === 'owner';
  const myBookings = bookings.filter(b => b.renter_id === user?.user_id);
  const receivedRequests = bookings.filter(b => b.owner_id === user?.user_id);

  // Stats
  const stats = isOwner ? [
    { label: 'Active Listings', value: properties.filter(p => p.is_active).length, icon: Building },
    { label: 'Pending Requests', value: receivedRequests.filter(b => b.status === 'pending').length, icon: Clock },
    { label: 'Total Bookings', value: receivedRequests.filter(b => b.status === 'approved' || b.status === 'completed').length, icon: CalendarCheck },
    { label: 'Messages', value: conversations.length, icon: MessageSquare }
  ] : [
    { label: 'Active Bookings', value: myBookings.filter(b => b.status === 'approved').length, icon: CalendarCheck },
    { label: 'Pending Requests', value: myBookings.filter(b => b.status === 'pending').length, icon: Clock },
    { label: 'Messages', value: conversations.length, icon: MessageSquare },
    { label: 'Total Spent', value: `$${myBookings.filter(b => b.payment_status === 'paid').reduce((sum, b) => sum + b.total_amount, 0).toLocaleString()}`, icon: DollarSign }
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-slate-600 mt-1">
              {isOwner ? 'Manage your properties and bookings' : 'Track your housing search'}
            </p>
          </div>
          {isOwner && (
            <Link to="/create-listing">
              <Button className="bg-coral hover:bg-coral-hover text-white rounded-full" data-testid="dashboard-create-listing">
                <Plus className="w-4 h-4 mr-2" />
                New Listing
              </Button>
            </Link>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-soft-gray rounded-lg flex items-center justify-center">
                  <stat.icon className="w-5 h-5 text-trust-blue" />
                </div>
              </div>
              <p className="font-heading text-2xl font-bold text-slate-900">{stat.value}</p>
              <p className="text-sm text-slate-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Subscription Status Card (Owner Only) */}
        {isOwner && (
          <div className="mb-8" data-testid="subscription-card">
            {subscription?.status === 'active' ? (
              <div className={`rounded-xl border-2 p-6 ${
                subscription.tier === 'concierge' 
                  ? 'border-trust-blue bg-blue-50' 
                  : subscription.tier === 'featured'
                    ? 'border-amber-300 bg-amber-50'
                    : 'border-slate-200 bg-white'
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      subscription.tier === 'concierge' 
                        ? 'bg-trust-blue' 
                        : subscription.tier === 'featured'
                          ? 'bg-amber-500'
                          : 'bg-slate-700'
                    }`}>
                      {subscription.tier === 'concierge' ? (
                        <Crown className="w-7 h-7 text-white" />
                      ) : subscription.tier === 'featured' ? (
                        <Star className="w-7 h-7 text-white" />
                      ) : (
                        <Home className="w-7 h-7 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading font-bold text-xl text-slate-900">
                          {subscription.tier === 'concierge' ? 'Concierge Onboarding' :
                           subscription.tier === 'featured' ? 'Featured Priority' :
                           'Standard Listing'}
                        </h3>
                        <Badge className="bg-green-500 text-white">Active</Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Renews: {subscription.current_period_end 
                            ? format(new Date(subscription.current_period_end), 'MMM d, yyyy')
                            : 'N/A'}
                        </span>
                        {subscription.cancel_at_period_end && (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                            Cancels at period end
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Link to="/pricing">
                      <Button variant="outline" size="sm" data-testid="change-plan-btn">
                        <CreditCard className="w-4 h-4 mr-2" />
                        Change Plan
                      </Button>
                    </Link>
                    {!subscription.cancel_at_period_end && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleCancelSubscription}
                        disabled={cancelLoading}
                        className="text-slate-500 hover:text-red-600"
                        data-testid="cancel-subscription-btn"
                      >
                        {cancelLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel'}
                      </Button>
                    )}
                  </div>
                </div>
                
                {subscription.featured_badge && (
                  <div className="mt-4 pt-4 border-t border-slate-200 flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-2 text-amber-600">
                      <Star className="w-4 h-4" />
                      Featured badge on all listings
                    </span>
                    {subscription.concierge && (
                      <span className="flex items-center gap-2 text-trust-blue">
                        <Crown className="w-4 h-4" />
                        Concierge support active
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-amber-400 flex items-center justify-center">
                      <AlertTriangle className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-xl text-slate-900">
                        No Active Subscription
                      </h3>
                      <p className="text-sm text-slate-600 mt-1">
                        Subscribe to publish listings and connect with renters
                      </p>
                    </div>
                  </div>
                  <Link to="/pricing">
                    <Button className="bg-trust-blue hover:bg-trust-blue-hover text-white" data-testid="subscribe-btn">
                      <Crown className="w-4 h-4 mr-2" />
                      View Plans
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        <Tabs defaultValue={isOwner ? 'listings' : 'bookings'}>
          <TabsList className="mb-6">
            {isOwner && <TabsTrigger value="listings" data-testid="tab-listings">My Listings</TabsTrigger>}
            <TabsTrigger value="bookings" data-testid="tab-bookings">
              {isOwner ? 'Booking Requests' : 'My Bookings'}
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          </TabsList>

          {/* Listings Tab (Owner) */}
          {isOwner && (
            <TabsContent value="listings">
              {properties.length === 0 ? (
                <div className="bg-soft-gray rounded-2xl p-12 text-center">
                  <Building className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                    No listings yet
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Create your first listing to start receiving booking requests.
                  </p>
                  <Link to="/create-listing">
                    <Button className="bg-trust-blue hover:bg-trust-blue-hover text-white rounded-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Listing
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {properties.map((property) => (
                    <div 
                      key={property.property_id}
                      className="bg-white rounded-xl border border-slate-200 p-4 flex gap-4 items-center hover:shadow-md transition-shadow"
                    >
                      <img
                        src={property.photos?.[0] || 'https://via.placeholder.com/100'}
                        alt={property.title}
                        className="w-24 h-24 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-semibold text-slate-900 truncate">
                          {property.title}
                        </h3>
                        <p className="text-sm text-slate-500">{property.address}, {property.city}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="font-semibold text-trust-blue">${property.price_per_month}/mo</span>
                          <Badge variant={property.is_active ? 'default' : 'secondary'}>
                            {property.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/property/${property.property_id}`}>
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Link to={`/edit-listing/${property.property_id}`}>
                          <Button variant="outline" size="sm">
                            <Settings className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            {(isOwner ? receivedRequests : myBookings).length === 0 ? (
              <div className="bg-soft-gray rounded-2xl p-12 text-center">
                <CalendarCheck className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  No bookings yet
                </h3>
                <p className="text-slate-600 mb-6">
                  {isOwner 
                    ? 'When renters request to book your property, they will appear here.'
                    : 'Browse listings and request to book your next temporary home.'}
                </p>
                {!isOwner && (
                  <Link to="/browse">
                    <Button className="bg-trust-blue hover:bg-trust-blue-hover text-white rounded-full">
                      Browse Listings
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {(isOwner ? receivedRequests : myBookings).map((booking) => (
                  <div 
                    key={booking.booking_id}
                    className="bg-white rounded-xl border border-slate-200 p-4"
                  >
                    <div className="flex gap-4 items-start">
                      {booking.property_photo && (
                        <img
                          src={booking.property_photo}
                          alt={booking.property_title}
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-heading font-semibold text-slate-900">
                              {booking.property_title || 'Property'}
                            </h3>
                            <p className="text-sm text-slate-500">
                              {format(new Date(booking.start_date), 'MMM d')} - {format(new Date(booking.end_date), 'MMM d, yyyy')}
                            </p>
                            <p className="text-sm text-slate-600 mt-1">
                              {isOwner ? `Renter: ${booking.renter_name}` : `Owner: ${booking.owner_name}`}
                            </p>
                          </div>
                          <Badge 
                            variant={
                              booking.status === 'approved' ? 'default' :
                              booking.status === 'pending' ? 'secondary' :
                              booking.status === 'rejected' ? 'destructive' : 'outline'
                            }
                          >
                            {booking.status}
                          </Badge>
                        </div>

                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                          <div className="flex items-center gap-4">
                            <span className="font-semibold text-slate-900">
                              ${booking.total_amount.toLocaleString()}
                            </span>
                            <Badge variant={booking.payment_status === 'paid' ? 'default' : 'secondary'}>
                              {booking.payment_status}
                            </Badge>
                          </div>

                          {isOwner && booking.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleBookingAction(booking.booking_id, 'rejected')}
                                data-testid={`reject-${booking.booking_id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Decline
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleBookingAction(booking.booking_id, 'approved')}
                                data-testid={`approve-${booking.booking_id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                            </div>
                          )}

                          {!isOwner && booking.status === 'approved' && booking.payment_status === 'unpaid' && (
                            <Link to={`/payment/${booking.booking_id}`}>
                              <Button size="sm" className="bg-coral hover:bg-coral-hover text-white">
                                Pay Now
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            {conversations.length === 0 ? (
              <div className="bg-soft-gray rounded-2xl p-12 text-center">
                <MessageSquare className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  No messages yet
                </h3>
                <p className="text-slate-600">
                  Start a conversation with {isOwner ? 'renters' : 'property owners'}.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {conversations.map((conv) => {
                  const otherParticipant = conv.participants.find(p => p.user_id !== user?.user_id);
                  return (
                    <Link
                      key={conv.conversation_id}
                      to={`/messages/${conv.conversation_id}`}
                      className="block bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center gap-4">
                        <Avatar>
                          <AvatarImage src={otherParticipant?.picture} />
                          <AvatarFallback className="bg-trust-blue text-white">
                            {otherParticipant?.name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-slate-900">{otherParticipant?.name}</h4>
                            {conv.unread_count > 0 && (
                              <Badge className="bg-coral text-white">{conv.unread_count}</Badge>
                            )}
                          </div>
                          {conv.property_title && (
                            <p className="text-sm text-trust-blue">{conv.property_title}</p>
                          )}
                          {conv.last_message && (
                            <p className="text-sm text-slate-500 truncate">{conv.last_message}</p>
                          )}
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default DashboardPage;
